import { useState } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { UserButton } from '@clerk/clerk-react';
import { useCountry } from '../contexts/CountryContext';
import { useAppAuth } from '../contexts/AuthContext';
import { cn, COUNTRY_FLAGS } from '../lib/utils';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  LayoutDashboard,
  Building2,
  Store,
  ClipboardList,
  Calendar,
  FileText,
  ShieldCheck,
  AlertTriangle,
  FolderOpen,
  Truck,
  Receipt,
  BarChart3,
  Settings,
  Menu,
  X,
  Bell,
  ChevronRight,
} from 'lucide-react';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/vendors', label: 'Vendors', icon: Building2 },
  { path: '/stores', label: 'Stores', icon: Store },
  { path: '/assignments', label: 'Assignments', icon: ClipboardList },
  { path: '/collections', label: 'Collections', icon: Calendar },
  { path: '/offers', label: 'Offers', icon: FileText },
  { path: '/compliance', label: 'Compliance', icon: ShieldCheck },
  { path: '/exceptions', label: 'Exceptions', icon: AlertTriangle },
  { path: '/documents', label: 'Documents', icon: FolderOpen },
  { path: '/service-requests', label: 'Service Requests', icon: Truck },
  { path: '/invoices', label: 'Invoices', icon: Receipt },
  { path: '/analytics', label: 'Analytics', icon: BarChart3 },
  { path: '/admin', label: 'Admin', icon: Settings, adminOnly: true },
];

export function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { countries, selectedCountry, setSelectedCountry } = useCountry();
  const { user, isAdmin } = useAppAuth();
  const location = useLocation();
  const countryOptions = Array.isArray(countries) ? countries : [];

  const filteredNavItems = navItems.filter(item => !item.adminOnly || isAdmin);

  const getPageTitle = () => {
    const current = navItems.find(item => location.pathname.startsWith(item.path));
    return current?.label || 'Dashboard';
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-border transform transition-transform duration-200 lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-border">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 3v18M3 12h18" />
                </svg>
              </div>
              <span className="font-bold text-foreground">MindYourWaste</span>
            </div>
            <button
              className="lg:hidden p-1 hover:bg-muted rounded"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Country Selector */}
          <div className="p-4 border-b border-border">
            <label className="text-xs font-medium text-muted-foreground mb-2 block">
              Country Filter
            </label>
            <Select
              value={selectedCountry || 'all'}
              onValueChange={(value) => setSelectedCountry(value === 'all' ? null : value)}
            >
              <SelectTrigger data-testid="country-selector" className="w-full">
                <SelectValue placeholder="All Countries" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Countries</SelectItem>
                {countryOptions.map((country) => (
                  <SelectItem key={country.code} value={country.code}>
                    <span className="flex items-center gap-2">
                      <span>{COUNTRY_FLAGS[country.code] || '🌍'}</span>
                      <span>{country.name}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4 space-y-1">
            {filteredNavItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) =>
                  cn('sidebar-link', isActive && 'active')
                }
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>

	          {/* User info */}
	          <div className="p-4 border-t border-border">
	            <div className="flex items-center gap-3 rounded-2xl border border-border bg-muted/40 px-3 py-3">
	              <UserButton afterSignOutUrl="/sign-in" />
	              <div className="flex-1 min-w-0">
	                <p className="text-sm font-medium text-foreground truncate">
	                  {user?.first_name || user?.email?.split('@')[0] || 'User'}
	                </p>
	                <div className="mt-1 flex items-center gap-2">
	                  <span className="inline-flex items-center rounded-full bg-white px-2 py-0.5 text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground border border-border">
	                    {user?.role?.replace('_', ' ') || 'Ops Manager'}
	                  </span>
	                </div>
	              </div>
	            </div>
	          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-16 bg-white border-b border-border flex items-center justify-between px-4 lg:px-6 sticky top-0 z-30">
          <div className="flex items-center gap-4">
            <button
              className="lg:hidden p-2 hover:bg-muted rounded-lg"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-toggle"
            >
              <Menu className="w-5 h-5" />
            </button>
            
            {/* Breadcrumb */}
            <nav className="flex items-center text-sm">
              <span className="text-muted-foreground">Home</span>
              <ChevronRight className="w-4 h-4 mx-2 text-muted-foreground" />
              <span className="font-medium text-foreground">{getPageTitle()}</span>
            </nav>
          </div>

          <div className="flex items-center gap-3">
            {/* Notifications */}
            <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
              <Bell className="w-5 h-5" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-destructive rounded-full" />
            </Button>

            {/* Selected country badge */}
            {selectedCountry && (
              <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-secondary rounded-full text-sm">
                <span>{COUNTRY_FLAGS[selectedCountry] || '🌍'}</span>
                <span className="font-medium">{selectedCountry}</span>
              </div>
            )}
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
